import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { Bell, CheckCheck } from 'lucide-react';
import {
  Notification, useMarkAllNotificationsRead, useMarkNotificationRead,
  useNotifications, useUnreadCount,
} from '@/lib/notifications';
import { useSocketEvent } from '@/lib/socket';

/**
 * Bell button with unread badge + dropdown panel of recent
 * notifications. Receives realtime updates via socket.io — when the
 * backend pushes `notification:new`, the unread count badge bumps and
 * the dropdown surfaces the new row at the top.
 */
export function NotificationBell() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  const notifications = useNotifications(false);
  const unread = useUnreadCount();
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();

  // Realtime — invalidate caches so unread count + dropdown both
  // refresh. We don't optimistically prepend because the server is
  // the source of truth and a refetch is cheap (≤30 rows).
  useSocketEvent<Notification>('notification:new', () => {
    qc.invalidateQueries({ queryKey: ['notifications'] });
    qc.invalidateQueries({ queryKey: ['notifications-unread-count'] });
  });

  // Click outside / Escape closes the popover.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const count = unread.data ?? 0;
  const items = notifications.data ?? [];

  return (
    <div className="relative" ref={popoverRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label={t('notifications.bellLabel')}
        className="relative inline-flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition"
      >
        <Bell className="h-4 w-4" />
        {count > 0 && (
          <span className="absolute -top-0.5 -end-0.5 grid place-items-center min-w-[16px] h-4 px-1 rounded-full bg-gradient-to-br from-rose-500 to-fuchsia-500 text-white text-[9px] font-semibold shadow-glow">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            className="absolute end-0 top-12 z-50 w-[360px] rounded-2xl glass-strong shadow-card-lift overflow-hidden"
          >
            <header className="flex items-center justify-between px-4 py-3 border-b border-border">
              <p className="text-sm font-semibold">{t('notifications.title')}</p>
              {count > 0 && (
                <button
                  onClick={() => markAll.mutate()}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                >
                  <CheckCheck className="h-3.5 w-3.5" />
                  {t('notifications.markAll')}
                </button>
              )}
            </header>

            <div className="max-h-[440px] overflow-y-auto keenez-scrollbar">
              {items.length === 0 ? (
                <p className="px-4 py-10 text-center text-sm text-muted-foreground">
                  {t('notifications.empty')}
                </p>
              ) : (
                <ul className="divide-y divide-border">
                  {items.slice(0, 30).map((n) => (
                    <NotificationRow key={n.id} n={n} onClick={() => { markRead.mutate(n.id); setOpen(false); }} />
                  ))}
                </ul>
              )}
            </div>

            <footer className="border-t border-border bg-muted/30">
              <Link
                to="/app"
                onClick={() => setOpen(false)}
                className="block text-center text-xs font-semibold text-foreground/70 hover:text-foreground py-2.5 transition"
              >
                {t('notifications.viewAll', { defaultValue: 'View all' })}
              </Link>
            </footer>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function NotificationRow({ n, onClick }: { n: Notification; onClick: () => void }) {
  const isUnread = !n.readAt;
  const content = (
    <>
      <div className="flex items-start gap-3">
        <span className={`mt-1 h-2 w-2 rounded-full shrink-0 ${isUnread ? 'bg-gradient-to-br from-indigo-500 to-fuchsia-500' : 'bg-transparent'}`} />
        <div className="flex-1 min-w-0">
          <p className={`text-sm leading-snug ${isUnread ? 'font-semibold' : 'font-medium text-foreground/80'}`}>{n.title}</p>
          {n.body && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.body}</p>}
          <p className="num text-[10px] text-muted-foreground mt-1">
            {new Date(n.createdAt).toLocaleString()}
          </p>
        </div>
      </div>
    </>
  );

  return (
    <li className="hover:bg-muted/30 transition">
      {n.linkPath ? (
        <Link to={n.linkPath} onClick={onClick} className="block px-4 py-3">{content}</Link>
      ) : (
        <button onClick={onClick} className="w-full text-start px-4 py-3">{content}</button>
      )}
    </li>
  );
}
